import { gql, useApolloClient } from '@apollo/client';
import { TextButton, Toast, useToast } from '@zillow/constellation-10';
import { useContext } from 'react';
import { SatelliteConversationMessageType } from '@zg-rentals/ts-satellite-client';
import { getCurrentSessionURL } from '../utils/fullStory';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useSatelliteWebLandlordConversationHandler_addSystemMessage,
  useSatelliteWebLandlordConversationHandler_getCachedConversationCounts,
  useSatelliteWebLandlordConversationHandler_markConversationAsUnread,
  useSatelliteWebLandlordConversationHandler_updateConversationAttributes,
} from '@zg-rentals/ts-satellite-client-react-query';
import {
  ConversationList_GetConversationsDocument,
  Folder,
  MessageList_GetConversationDocument,
  ReportConversationReason,
  useConversationActionsHook_MarkConversationAsReadMutation,
  useConversationActionsHook_UpdateConversationStatusMutation,
} from '../types/GraphqlTypes';
import { MAX_OLD_CONVERSATION_DAYS } from '../constants/ConfigConstants';
import { inboxPagePreventMarkAsReadVariable } from '../lib/apollo';
import { AppContext } from '../contexts/AppContextProvider';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import useConversationListHelpers from './useConversationListHelpers';
import useInboxRouterHelper from './useInboxRouterHelper';
import useModal from './useModal';
import { reportReasonClickstreamEventMap } from '../constants/clickstreamEvents/reportProblemModal';
import { TRIGGER_LOCATION } from '../constants/clickstreamEvents/globals';
import { logApolloError } from '../utils/errorUtils';
import { appMarkerLogger } from '../bootstrap';
import {
  emitIreneArchiveMessageEvent,
  emitIreneMarksMessageUnreadEvent,
  emitIreneUnarchiveMessageEvent,
} from '../constants/clickstreamEvents/conversationActions';
import { useAuth } from '@zg-rentals/auth-comet';
import { useRefetchQueriesWithRetry } from './useRefetchQueriesWithRetry';

type ConversationActionArgs = {
  conversationIdToUpdate?: string;
  listingAliasToUpdate?: string;
  retry?: boolean;
  isUndoAction?: boolean;
};

type ReportConversationArgs = {
  conversationIdToUpdate: string;
  reportReasonType: ReportConversationReason;
  userReportComment?: string;
  isUndoAction?: boolean;
};

type GetUpdateConversationFunctionArgs = {
  csEvent?: () => void;
  toastBodySuccessText: string;
  toastBodyErrorText: string;
  isArchived?: boolean;
  reportReasonType?: ReportConversationReason;
  userReportComment?: string;
  onUndoClick(args: ConversationActionArgs): void;
};
const TOAST_TEXT_ARCHIVE = 'Conversation archived';
const TOAST_TEXT_REPORTED = 'Conversation reported';
const TOAST_TEXT_MOVED_TO_INBOX = 'Conversation moved to inbox';
const TOAST_TEXT_UNREAD = 'Conversation marked as unread';

const logger = appMarkerLogger.child({ name: 'useConversationActions' });

/**
 *
 * @description useConversationActions offers helpers to perform the following actions on a conversation
 * - Archive
 * - Report (spam)
 * - Move to inbox (from archive or reported (spam))
 * - Mark as read
 * - Mark as unread
 */
export default function () {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const { changeConversation } = useInboxRouterHelper();
  const { getConversationToRedirectToNext, removeConversationFromList } = useConversationListHelpers();
  const { user } = useAuth();
  const { enqueueToast } = useToast();
  const { clearModal } = useModal();
  const fullStorySessionUrl = getCurrentSessionURL();
  const {
    conversationId,
    daysBack,
    listingAlias,
    folder,
    renterProgressStatus,
    readRepliedStatus,
    isByProperty,
    clientTimeZone,
  } = useContext(InboxPageContext);
  const { isImpersonator } = useContext(AppContext);

  const refetchConversationQueries = useRefetchQueriesWithRetry({
    errorContext: 'hook.useConversationActions.refetchQueries',
  });

  const [updateConversationStatus] = useConversationActionsHook_UpdateConversationStatusMutation();

  // When you undo a status update, user expects that conversation to re-appear in ConversationList
  // Manually refetch ConversationList for this use case
  const [undoUpdateConversationStatus] = useConversationActionsHook_UpdateConversationStatusMutation({
    refetchQueries: [
      {
        query: ConversationList_GetConversationsDocument,
        variables: {
          folder,
          daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
          listingAlias: isByProperty ? listingAlias : null,
          renterProgressStatus,
          readRepliedStatus,
          clientTimeZone,
        },
      },
    ],
  });

  const { mutate: markAsUnread } = useSatelliteWebLandlordConversationHandler_markConversationAsUnread({
    meta: {
      invalidates: ['satellite:conversationCounts'],
      context: {
        api: 'markConversationAsUnread',
        details: 'useConversationActions - marking conversation as unread',
      },
    },
  });

  const [markConversationAsReadMutation, { error: markConversationAsReadError }] =
    useConversationActionsHook_MarkConversationAsReadMutation();

  const { mutateAsync: updateConversationAttributes } =
    useSatelliteWebLandlordConversationHandler_updateConversationAttributes({
      meta: {
        context: {
          api: 'updateConversationAttributes',
          details: 'useConversationActions - Error marking conversation as favorite or unfavorite',
        },
      },
    });

  const { mutate: addSystemMessage, error: addSystemMessageError } =
    useSatelliteWebLandlordConversationHandler_addSystemMessage({
      meta: {
        context: {
          api: 'addSystemMessage',
          details: 'useConversationActions - Error adding system message to conversation',
          fullStorySessionUrl,
        },
      },
    });

  function getUpdateConversationFunction({
    csEvent,
    toastBodySuccessText,
    toastBodyErrorText,
    isArchived,
    reportReasonType,
    userReportComment,
    onUndoClick,
  }: GetUpdateConversationFunctionArgs): (args?: ConversationActionArgs) => Promise<void> {
    const updateConversation = async ({
      conversationIdToUpdate = conversationId,
      listingAliasToUpdate = listingAlias,
      retry = false,
      isUndoAction = false,
    }: ConversationActionArgs = {}): Promise<void> => {
      if (isImpersonator) return;

      if (!conversationIdToUpdate) {
        logger.warn('No conversationId provided when trying to update conversation status');
        return;
      }

      csEvent?.();

      const updateStatusMutation = isUndoAction ? undoUpdateConversationStatus : updateConversationStatus;

      await updateStatusMutation({
        variables: {
          conversationId: conversationIdToUpdate,
          isArchived,
          reportReasonType,
          userReportComment,
        },
        onCompleted() {
          if (isUndoAction) {
            // Go back to conversation that was un-done
            changeConversation({
              conversationId: conversationIdToUpdate,
              listingAlias: listingAliasToUpdate || '',
            });
          } else {
            // Remove updated conversation from the ConversationList Query
            const { conversations } = removeConversationFromList(conversationIdToUpdate);
            // Find conversation to redirect to next
            const { nextConversationId, nextListingAlias } = getConversationToRedirectToNext(
              conversationId || '',
              conversations,
            );

            changeConversation({
              conversationId: nextConversationId,
              listingAlias: nextListingAlias,
            });
          }

          enqueueToast(
            <Toast
              tone="success"
              actionButton={
                !isUndoAction && (
                  <TextButton
                    onClick={(event) => {
                      event.stopPropagation();
                      return onUndoClick({ isUndoAction: true });
                    }}
                    onImpact
                  >
                    Undo
                  </TextButton>
                )
              }
            >
              {toastBodySuccessText}
            </Toast>,
          );
        },
        onError(error) {
          logApolloError(error, {
            details: 'useConversationActions - Error updating conversation',
            conversationIdToUpdate,
            listingAliasToUpdate,
            status: {
              isArchived,
              reportReasonType,
              userReportComment,
            },
          });

          if (!retry) {
            logger.info('retrying updating conversation');
            return updateConversation({
              conversationIdToUpdate,
              listingAliasToUpdate,
              retry: true,
            });
          }

          enqueueToast(<Toast tone="critical">{toastBodyErrorText}</Toast>);
        },
      });
    };

    return updateConversation;
  }

  function reportConversation(args: ReportConversationArgs) {
    if (isImpersonator) return;

    const { reportReasonType, userReportComment } = args;
    const reportReasonTypeMappingFn =
      reportReasonClickstreamEventMap[
        reportReasonType as Exclude<ReportConversationReason, ReportConversationReason.NotReported>
      ];

    if (reportReasonTypeMappingFn) {
      reportReasonTypeMappingFn(user, TRIGGER_LOCATION.IreneInbox);
    }

    return getUpdateConversationFunction({
      toastBodySuccessText: TOAST_TEXT_REPORTED,
      toastBodyErrorText: 'There was an error reporting this conversation',
      reportReasonType,
      userReportComment,
      onUndoClick: () => {
        undoReportConversation({ ...args, isUndoAction: true });
      },
    })(args);
  }

  function undoReportConversation(args: ReportConversationArgs) {
    if (isImpersonator) return;

    return getUpdateConversationFunction({
      toastBodySuccessText: TOAST_TEXT_MOVED_TO_INBOX,
      toastBodyErrorText: 'There was an error moving conversation back to your inbox',
      reportReasonType: ReportConversationReason.NotReported,
      onUndoClick: () => {
        reportConversation({ ...args, isUndoAction: true });
      },
    })(args);
  }

  function archiveConversation(args?: ConversationActionArgs) {
    if (isImpersonator) return;

    return getUpdateConversationFunction({
      csEvent: () => emitIreneArchiveMessageEvent(user),
      toastBodySuccessText: TOAST_TEXT_ARCHIVE,
      toastBodyErrorText: 'There was an error marking this conversation as archived',
      isArchived: true,
      onUndoClick: () => {
        undoArchiveConversation({ ...args, isUndoAction: true });
      },
    })(args);
  }

  function undoArchiveConversation(args?: ConversationActionArgs) {
    if (isImpersonator) return;

    return getUpdateConversationFunction({
      csEvent: () => emitIreneUnarchiveMessageEvent(user),
      toastBodySuccessText: TOAST_TEXT_MOVED_TO_INBOX,
      toastBodyErrorText: 'There was an error moving conversation back to your inbox',
      isArchived: false,
      onUndoClick: () => {
        archiveConversation({ ...args, isUndoAction: true });
      },
    })(args);
  }

  const markConversationAsUnread = ({
    conversationIdToUpdate = conversationId,
  }: {
    conversationIdToUpdate?: string;
  } = {}): void => {
    if (isImpersonator) return;

    if (!conversationIdToUpdate) {
      logger.warn('No conversationId provided when trying to mark conversation as unread');
      return;
    }

    emitIreneMarksMessageUnreadEvent(user);

    markAsUnread(
      { postData: { conversationId: conversationIdToUpdate } },
      {
        onSuccess: () => {
          inboxPagePreventMarkAsReadVariable(true);

          apolloClient.cache.writeFragment({
            id: apolloClient.cache.identify({
              conversationId: conversationIdToUpdate,
              __typename: 'ZRM_Conversation',
            }),
            fragment: gql`
              fragment ReadStatus on ZRM_Conversation {
                hasUnreadMessage
              }
            `,
            data: {
              hasUnreadMessage: true,
            },
          });

          enqueueToast(<Toast tone="success">{TOAST_TEXT_UNREAD}</Toast>);
        },
        onError: () => {
          enqueueToast(<Toast tone="critical">There was an error marking this conversation as unread</Toast>);
        },
      },
    );
  };

  const markConversationAsRead = async (lastReadMs: string) => {
    if (isImpersonator) return;

    if (!conversationId) {
      // Should never happen, function should only be called when conversationId exists
      logger.warn('No conversationId provided when trying to mark conversation as unread');
      return;
    }

    await markConversationAsReadMutation({
      variables: {
        conversationId,
        lastReadMs,
      },
    });

    if (markConversationAsReadError) {
      logApolloError(markConversationAsReadError, {
        details: 'useConversationActions - Error marking conversation as read in useConversationActions',
        conversationId,
        listingAlias,
        lastReadMs,
      });
      return;
    }

    apolloClient.cache.writeFragment({
      id: apolloClient.cache.identify({ conversationId, __typename: 'ZRM_Conversation' }),
      fragment: gql`
        fragment ReadStatus on ZRM_Conversation {
          hasUnreadMessage
        }
      `,
      data: {
        hasUnreadMessage: false,
      },
    });

    // Invalidate badge counts cache after marking conversation as read
    // TODO: Once markConversationAsRead is migrated to React Query, replace this with
    // meta.invalidates pattern for automatic cache invalidation
    queryClient.invalidateQueries({
      queryKey: useSatelliteWebLandlordConversationHandler_getCachedConversationCounts.getKey(),
    });
  };
  const markConversationAsFavoriteOrUnfavorite = async (conversationIdToUpdate: string, isFavorite: boolean) => {
    if (isImpersonator) return;

    await updateConversationAttributes({
      postData: {
        conversationId: conversationIdToUpdate,
        isFavorite: !isFavorite,
      },
    });

    apolloClient.cache.modify({
      id: apolloClient.cache.identify({
        __typename: 'ZRM_Conversation',
        conversationId: conversationIdToUpdate,
      }),
      fields: {
        status(cachedStatus: { isFavorite?: boolean }) {
          return {
            ...cachedStatus,
            isFavorite: !isFavorite,
          };
        },
      },
    });

    // Remove the conversation from the cached list of conversations marked as favorite
    // And change to next conversation if user is in Folder.Favorite (Starred folder) and is unstarring the selected conversation
    if (isFavorite) {
      if (folder === Folder.Favorite && conversationIdToUpdate === conversationId) {
        const { conversations } = removeConversationFromList(conversationIdToUpdate, Folder.Favorite);
        const { nextConversationId, nextListingAlias } = getConversationToRedirectToNext(
          conversationIdToUpdate,
          conversations,
        );

        changeConversation({
          conversationId: nextConversationId,
          listingAlias: nextListingAlias,
        });
      } else {
        removeConversationFromList(conversationIdToUpdate, Folder.Favorite);
      }
    }
  };

  const requestRenterProfile = (overrides?: {
    conversationId?: string;
    listingAlias?: string;
    clientTimeZone?: string;
  }) => {
    const targetConversationId = overrides?.conversationId ?? conversationId;
    const targetListingAlias = overrides?.listingAlias ?? listingAlias;
    const targetClientTimeZone = overrides?.clientTimeZone ?? clientTimeZone;

    if (isImpersonator || !targetConversationId) return;

    addSystemMessage(
      {
        postData: {
          conversationId: targetConversationId,
          messageType: SatelliteConversationMessageType.RENTER_PROFILE_REQUEST,
        },
      },
      {
        onSuccess: () => {
          clearModal();
          enqueueToast(<Toast tone="success">Request sent</Toast>);

          // Refetch conversation to get the new system message
          refetchConversationQueries([
            {
              query: MessageList_GetConversationDocument,
              variables: {
                conversationId: targetConversationId,
                listingAlias: targetListingAlias || undefined,
                clientTimeZone: targetClientTimeZone,
              },
              name: 'MessageList_GetConversation',
            },
          ]);
        },
        onError: () => {
          enqueueToast(<Toast tone="critical">There was an error sending your request. Please try again.</Toast>);
        },
      },
    );
  };

  return {
    archiveConversation,
    reportConversation,
    requestRenterProfile,
    undoArchiveConversation,
    undoReportConversation,
    markConversationAsUnread,
    markConversationAsRead,
    markConversationAsFavoriteOrUnfavorite,
    hasErrorOnRequestRenterProfile: Boolean(addSystemMessageError),
  };
}
